import React, { useEffect, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, CardContent } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import CardHeader from '@material-ui/core/CardHeader';
import Card from '@material-ui/core/Card';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { atomOneDark } from 'react-syntax-highlighter/dist/cjs/styles/hljs';
import SyntaxHighlighter from 'react-syntax-highlighter';
import Typography from '@material-ui/core/Typography';
import { getConnectionCurlCollection } from '../../Services/ConnectionService';
import { startsWith, isEmpty, round } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Skeleton from '@material-ui/lab/Skeleton';

const useStyles = makeStyles(() => ({
  capitalize: {
    textTransform: 'capitalize',
  },
  formControl: {
    minWidth: '100px',
  },
  displayInlineBlock: {
    display: 'inline-block',
  },
  statuscodeWrapper: {
    padding: '2px 5px',
    borderRadius: '5px',
  },
  statuscodeError: {
    backgroundColor: 'rgba(255, 16, 55, .3)',
  },
  statuscodeSuccess: {
    backgroundColor: 'rgba(86, 199, 98, .55)',
  },
  statuscodeUnknown: {
    backgroundColor: 'rgba(70, 75, 71, .55)',
  },
  fontBold: {
    fontWeight: '700',
  },
  flexColumn: {
    flexDirection: 'column',
  },
  mb2: {
    marginBottom: '.5rem',
  },
  mt8: {
    marginTop: '2rem',
  },
  fullWidth: {
    width: '100%',
  },
}));

const CurlDetails = ({ connectionId, curlId }) => {
  const [curlCollection, setCurlCollection] = useState([]);
  const classes = useStyles();
  const intl = useIntl();

  const getCurlCollection = async () => {
    const response = await getConnectionCurlCollection(connectionId, curlId);
    const curlData = response.data;
    if ('options' in curlData && 'headers' in curlData.options && 'Authorization' in curlData.options.headers) {
      curlData.options.headers.Authorization = '<hidden>';
    }
    setCurlCollection(curlData);
  };

  const determineLanguage = (content) => {
    if (!content) {
      return 'text';
    }

    if (startsWith(content, '<!DOCTYPE html')) {
      return 'html';
    }

    try {
      JSON.parse(content);

      return 'json';
    } catch (err) {}

    return 'xml';
  };

  useEffect(() => {
    getCurlCollection();
  }, []);

  return (
    <div>
      <Grid container>
        <Grid item xs={12}>
          <Card>
            <CardHeader title={ intl.formatMessage({ id: 'curlDetails.title'})} />
            <CardContent>
              <Grid container>
                <Grid item xs={12}>
                  <Typography display="inline" className={classes.fontBold}>{ intl.formatMessage({ id: 'curlDetails.url' }) + ': ' }</Typography>
                  <Typography display="inline">{ curlCollection?.url ? curlCollection.url : <Skeleton className={classes.displayInlineBlock} width={250} />}</Typography>
                </Grid>
                <Grid item xs={12} lg={4}>
                  <Typography display="inline" className={classes.fontBold}>{ intl.formatMessage({ id: 'curlDetails.statuscode' }) + ': ' }</Typography>
                  <div className={ `${classes.displayInlineBlock} ${classes.statuscodeWrapper} ${curlCollection?.response?.statusCode ? startsWith(curlCollection.response.statusCode, '5') || startsWith(curlCollection.response.statusCode, '4') ? classes.statuscodeError : classes.statuscodeSuccess : classes.statuscodeUnknown}` }>
                    <Typography>{ curlCollection?.response?.statusCode ? curlCollection.response.statusCode : 'unknown'}</Typography>
                  </div>
                </Grid>
                <Grid item xs={12} lg={4}>
                  <Typography display="inline" className={classes.fontBold}>{ intl.formatMessage({ id: 'curlDetails.method' }) + ': ' }</Typography>
                  <Typography display="inline">{ curlCollection?.method ? curlCollection.method : <Skeleton className={classes.displayInlineBlock} className={classes.displayInlineBlock} width={40} />}</Typography>
                </Grid>
                <Grid item xs={12} lg={4}>
                  <Typography display="inline" className={classes.fontBold}>{ intl.formatMessage({ id: 'curlDetails.duration' }) + ': ' }</Typography>
                  <Typography display="inline">{ curlCollection?.response?.info?.total_time ? `${round(curlCollection?.response?.info?.total_time, 2)} seconds` : <Skeleton className={classes.displayInlineBlock} className={classes.displayInlineBlock} width={40} />}</Typography>
                </Grid>
              </Grid>
              <Grid container className={classes.mt8}>
                <Grid item xs={12}>
                  <Typography variant="h5" className={classes.mb2}>
                    <FormattedMessage id="curlDetails.request" />
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  { !curlCollection?.options?.headers && !curlCollection?.options?.body && !curlCollection?.options?.query && (<Skeleton height={144} />)}
                  { curlCollection?.options?.headers && (<Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography className={classes.fontBold}>
                        <FormattedMessage id="curlDetails.request.headers" />
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails className={classes.flexColumn}>
                      { curlCollection?.options?.headers && Object.entries(curlCollection.options.headers).map((header, index) => {
                        return (
                          <div key={index}>
                            <Typography display="inline" className={classes.fontBold}>{ `${header[0]}: ` }</Typography>
                            <Typography display="inline">{ header[1] }</Typography>
                          </div>
                        )
                      })}
                    </AccordionDetails>
                  </Accordion> )}
                  { curlCollection?.options?.body && (<Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography className={classes.fontBold}>
                        <FormattedMessage id="curlDetails.request.body" />
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <div className={classes.fullWidth}>
                        { curlCollection?.options?.body ?
                          <SyntaxHighlighter language={determineLanguage(curlCollection.options.body)} style={atomOneDark}>
                            { determineLanguage(curlCollection.options.body) === 'json' ? JSON.stringify(JSON.parse(curlCollection.options.body), null, 2) : curlCollection.options.body }
                          </SyntaxHighlighter> : curlCollection.options.body
                        }
                      </div>
                    </AccordionDetails>
                  </Accordion> )}
                  { !isEmpty(curlCollection?.options?.query) && (<Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography className={classes.fontBold}>
                        <FormattedMessage id="curlDetails.request.url_params" />
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails className={classes.flexColumn}>
                      { curlCollection?.options?.query && Object.entries(curlCollection.options.query).map((param, index) => {
                        return (
                          <div>
                            <Typography display="inline" className={classes.fontBold}>{ `${param[0]}: ` }</Typography>
                            <Typography display="inline">{ param[1] }</Typography>
                          </div>
                        )
                      })}
                    </AccordionDetails>
                  </Accordion> )}
                </Grid>
              </Grid>
              <Grid container className={classes.mt8}>
                <Grid item xs={12}>
                  <Typography variant="h5" className={classes.mb2}>
                    <FormattedMessage id="curlDetails.response" />
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  { !curlCollection?.response?.headers && !curlCollection?.response?.body && !curlCollection?.options?.query && (<Skeleton height={96} />)}
                  { curlCollection?.response?.headers && (<Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography className={classes.fontBold}>
                        <FormattedMessage id="curlDetails.response.headers" />
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails className={classes.flexColumn}>
                      { curlCollection?.response?.headers && Object.entries(curlCollection.response.headers).map((header, index) => {
                        return (
                          <div key={index}>
                            <Typography display="inline" className={classes.fontBold}>{ `${header[0]}: ` }</Typography>
                            <Typography display="inline">{ header[1] }</Typography>
                          </div>
                        )
                      })}
                    </AccordionDetails>
                  </Accordion> )}
                  { curlCollection?.response?.content && (<Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography className={classes.fontBold}>
                        <FormattedMessage id="curlDetails.response.content" />
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <div className={classes.fullWidth}>
                        { curlCollection?.response?.content && (
                          <SyntaxHighlighter language={determineLanguage(curlCollection.response.content)} style={atomOneDark}>
                            { determineLanguage(curlCollection.response.content) === 'json' ? JSON.stringify(JSON.parse(curlCollection.response.content), null, 2) : curlCollection.response.content }
                          </SyntaxHighlighter>)
                        }
                      </div>
                    </AccordionDetails>
                  </Accordion> )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

export default CurlDetails;
